import React, { useState, useEffect, useReducer, useContext } from 'react';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import SearchIcon from '@material-ui/icons/Search';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import EditIcon from '@material-ui/icons/Edit';
import api from '../../services/api';
import ConfirmationModal from '../../components/ConfirmationModal/';
import { i18n } from '../../translate/i18n';
import MainHeader from '../../components/MainHeader';
import Title from '../../components/Title';
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper';
import MainContainer from '../../components/MainContainer';
import toastError from '../../errors/toastError';
import { AuthContext } from '../../context/Auth/AuthContext';
import { Can } from '../../components/Can';
import NewTicketModal from '../../components/NewTicketModal';
import WebhookModal from '../../components/WebhookModal';
import { AddCircle, Build, WebhookOutlined } from '@mui/icons-material';
import { CircularProgress, Grid, Stack, Typography, Chip } from '@mui/material';

const reducer = (state, action) => {
  if (action.type === 'LOAD_CONTACTS') {
    const contacts = action.payload;
    const newContacts = [];

    contacts &&
      contacts.forEach((contact) => {
        const contactIndex = state.findIndex((c) => c.id === contact.id);
        if (contactIndex !== -1) {
          state[contactIndex] = contact;
        } else {
          newContacts.push(contact);
        }
      });

    return [...state, ...newContacts];
  }

  if (action.type === 'UPDATE_CONTACTS') {
    const contact = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contact.id);

    if (contactIndex !== -1) {
      state[contactIndex] = contact;
      return [...state];
    } else {
      return [contact, ...state];
    }
  }

  if (action.type === 'DELETE_CONTACT') {
    const contactId = action.payload;

    const contactIndex = state.findIndex((c) => c.id === contactId);
    if (contactIndex !== -1) {
      state.splice(contactIndex, 1);
    }
    return [...state];
  }

  if (action.type === 'RESET') {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 16,
    padding: theme.spacing(2),
    boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
    overflowY: 'scroll',
    ...theme.scrollbarStyles,
  },
  searchField: {
    width: '300px',
    '& .MuiOutlinedInput-root': {
      borderRadius: 40,
      backgroundColor: theme.palette.background.paper,
    },
  },
  addButton: {
    borderRadius: 40,
    textTransform: 'none',
    padding: '8px 24px',
  },
  tableHeader: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    borderRadius: 8,
    marginBottom: theme.spacing(2),
  },
  headerCell: {
    color: theme.palette.text.secondary,
    fontWeight: 600,
    fontSize: '0.875rem',
  },
  row: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8,
    marginBottom: theme.spacing(1),
    transition: 'all 0.2s ease',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px 0 rgba(0,0,0,0.05)',
    },
  },
  webhookIcon: {
    color: theme.palette.primary.main,
    marginRight: theme.spacing(1),
  },
  webhookName: {
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  statusChip: {
    borderRadius: 20,
    fontWeight: 500,
  },
  actionButton: {
    padding: 8,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  actionIcon: {
    fontSize: 20,
  },
}));

const Webhook = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState('');
  const [contacts, dispatch] = useReducer(reducer, []);
  const [webhooks, setWebhooks] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [selectedWebhookName, setSelectedWebhookName] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [contactTicket, setContactTicket] = useState({});
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [reloadData, setReloadData] = useState(false);

  useEffect(() => {
    dispatch({ type: 'RESET' });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get('/webhook/list', {
            params: { pageNumber },
          });
          setWebhooks(data.webhooks);
          dispatch({ type: 'LOAD_CONTACTS', payload: data.webhooks });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, reloadData]);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(false);
  };

  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  const hadleEditContact = (contactId, nameWebhook) => {
    setSelectedContactId(contactId);
    setSelectedWebhookName(nameWebhook);
    setContactModalOpen(true);
  };

  const handleDeleteWebhook = async (webhookId) => {
    try {
      await api.delete(`/webhook/deleteWebHook/${webhookId}`).then((res) => {
        setDeletingContact(null);
        setReloadData((old) => !old);
      });
      toast.success(i18n.t('webhookModal.toasts.deleted'));
    } catch (err) {
      toastError(err);
    }
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t('webhook.title')}</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            className={classes.searchField}
            placeholder={i18n.t('contacts.searchPlaceholder')}
            variant="outlined"
            size="small"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            className={classes.addButton}
            onClick={handleOpenContactModal}
          >
            <AddCircle sx={{ mr: 1 }} />
            {i18n.t('Adicionar Webhook')}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>
        <div className={classes.tableHeader}>
          <Grid container>
            <Grid item xs={3}>
              <Typography className={classes.headerCell}>
                {i18n.t('contacts.table.name')}
              </Typography>
            </Grid>
            <Grid item xs={2} align="center">
              <Typography className={classes.headerCell}>Status</Typography>
            </Grid>
            <Grid item xs={2} align="center">
              <Typography className={classes.headerCell}>Requisições do mês</Typography>
            </Grid>
            <Grid item xs={2} align="center">
              <Typography className={classes.headerCell}>Requisições totais</Typography>
            </Grid>
            <Grid item xs={3} align="center">
              <Typography className={classes.headerCell}>
                {i18n.t('contacts.table.actions')}
              </Typography>
            </Grid>
          </Grid>
        </div>

        {webhooks.map((contact) => (
          <div key={contact.id} className={classes.row}>
            <Grid container alignItems="center">
              <Grid item xs={3}>
                <Stack direction="row" alignItems="center">
                  <WebhookOutlined className={classes.webhookIcon} />
                  <Typography className={classes.webhookName}>
                    {contact.name}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={2} align="center">
                <Chip
                  label={contact.active ? 'Ativo' : 'Desativado'}
                  className={classes.statusChip}
                  color={contact.active ? 'success' : 'default'}
                  size="small"
                />
              </Grid>
              <Grid item xs={2} align="center">
                <Typography>{contact.requestMonth}</Typography>
              </Grid>
              <Grid item xs={2} align="center">
                <Typography>{contact.requestAll}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Stack direction="row" justifyContent="center" spacing={1}>
                  <IconButton
                    className={classes.actionButton}
                    onClick={() => hadleEditContact(contact.id, contact.name)}
                  >
                    <EditIcon className={classes.actionIcon} color="primary" />
                  </IconButton>
                  <IconButton
                    className={classes.actionButton}
                    onClick={() => history.push(`/webhook/${contact.hash_id}`)}
                  >
                    <Build className={classes.actionIcon} color="primary" />
                  </IconButton>
                  <Can
                    role={user.profile}
                    perform="contacts-page:deleteContact"
                    yes={() => (
                      <IconButton
                        className={classes.actionButton}
                        onClick={() => {
                          setConfirmOpen(true);
                          setDeletingContact(contact);
                        }}
                      >
                        <DeleteOutlineIcon className={classes.actionIcon} color="error" />
                      </IconButton>
                    )}
                  />
                </Stack>
              </Grid>
            </Grid>
          </div>
        ))}

        {loading && (
          <Stack justifyContent="center" alignItems="center" minHeight="50vh">
            <CircularProgress />
          </Stack>
        )}
      </Paper>

      <NewTicketModal
        modalOpen={newTicketModalOpen}
        initialContact={contactTicket}
        onClose={handleCloseOrOpenTicket}
      />

      <WebhookModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        webhookId={selectedContactId}
        nameWebhook={selectedWebhookName}
        onSave={() => setReloadData((old) => !old)}
      />

      <ConfirmationModal
        title={deletingContact ? `${i18n.t('contacts.confirmationModal.deleteTitle')} ${deletingContact.name}?` : `${i18n.t('contacts.confirmationModal.importTitlte')}`}
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => handleDeleteWebhook(deletingContact.id)}
      >
        {i18n.t('webhookModal.deleteMessage')}
      </ConfirmationModal>
    </MainContainer>
  );
};

export default Webhook;